import { useEffect, useRef, useState } from "react"
import { Form } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { isServerId } from "../../../mappers/toApiPayload"
import { useWizardContext } from "../../../WizardContext"
import { LengthAndRates } from "../../scheduling/LengthAndRates/LengthAndRates"

/**
 * Manages the local price list for the private listing Pricing tab.
 *
 * Owns `localPrices` state (seeded from form.getFieldValue("localPrices") which
 * WizardProvider initialises via buildDefaultValues → trip.trip_prices).
 *
 * Keeps form field "takenDurations" in sync so ExperienceDuration can disable
 * already-used durations in the dropdown.
 */
export const PrivateBookingOptions = ({ form }) => {
  const { t } = useTranslation()
  const { isFormInitialized } = useWizardContext()
  const hasSeeded = useRef(false)

  const [localPrices, setLocalPrices] = useState(() => form.getFieldValue("localPrices") || [])
  const [expandedRowData, setExpandedRowData] = useState(null)
  const [isShowErrorMessage, setIsShowErrorMessage] = useState(false)

  // localPrices is seeded from form.getFieldValue, but WizardProvider populates
  // that from trip.trip_prices slightly after mount. A mount-time read can miss
  // it, so re-seed once the form is initialized (the reliable wizard signal).
  useEffect(() => {
    if (!isFormInitialized || hasSeeded.current) return
    setLocalPrices(form.getFieldValue("localPrices") || [])
    hasSeeded.current = true
  }, [isFormInitialized, form])

  const activePricingType = Form.useWatch("pricing_type", form)

  // Keep form in sync so the save handler and ExperienceDuration can read these.
  // Only include durations from the active pricing model so switching models doesn't
  // block duration options that have no entry yet under the new model.
  useEffect(() => {
    form.setFieldValue("localPrices", localPrices)
    const activeDurations = localPrices
      .filter(
        (p) =>
          !p._destroy &&
          p.trip_duration != null &&
          (!p.pricing_type || !activePricingType || p.pricing_type === activePricingType)
      )
      .map((p) => p.trip_duration)
    form.setFieldValue("takenDurations", activeDurations)
  }, [localPrices, form, activePricingType])

  const handlePriceSave = (priceData) => {
    setLocalPrices((prev) => {
      if (priceData.id && isServerId(priceData.id)) {
        return prev.map((p) => (p.id === priceData.id ? { ...p, ...priceData } : p))
      }
      if (priceData.tempId) {
        return prev.map((p) => (p.tempId === priceData.tempId ? { ...p, ...priceData } : p))
      }
      return [...prev, { tempId: Date.now(), ...priceData }]
    })
  }

  const handlePriceDelete = ({ id, tempId }) => {
    setLocalPrices((prev) => {
      if (id && isServerId(id)) {
        return prev.map((p) => (p.id === id ? { ...p, _destroy: true } : p))
      }
      return prev.filter((p) => p.tempId !== tempId)
    })
  }

  // Show only options that belong to the active pricing model so options created
  // under the other model don't linger after switching (they're dropped on save).
  // Options with no stored pricing_type are freshly added — always show them.
  const displayPrices = localPrices.filter(
    (price) =>
      !price._destroy && (!price.pricing_type || !activePricingType || price.pricing_type === activePricingType)
  )

  return (
    <LengthAndRates
      form={form}
      isShowErrorMessage={isShowErrorMessage}
      setIsShowErrorMessage={setIsShowErrorMessage}
      localPrices={displayPrices}
      onPriceSave={handlePriceSave}
      onPriceDelete={handlePriceDelete}
      expandedRowData={expandedRowData}
      setExpandedRowData={setExpandedRowData}
      cardTitle={t("experiences.pricing.private.pricing_model")}
      addButtonLabel={t("experiences.pricing.private.add_option")}
      bookingOptionsTitle={t("experiences.pricing.private.booking_options_title")}
      bookingOptionsSubtitle={t("experiences.pricing.private.booking_options_subtitle")}
    />
  )
}

PrivateBookingOptions.propTypes = {
  form: PropTypes.object.isRequired,
}
